import { HttpContext, httpResource } from '@angular/common/http';
import { Service, computed } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { ResponseApi } from '../../../core/dtos/response-api';
import { HANDLE_HTTP_ERROR_LOCALLY } from '../../../core/http/http-context-tokens';

const RECURSO = `${environment.apiUrl}/sai/cartaservico/publico/identidade`;

/** A imagem do brasão publicado. A API escolhe entre o do Cabeçalho do adm e o do SAI. */
export const URL_BRASAO_PUBLICO = `${RECURSO}/brasao`;

/** O erro fica local: sem identidade, o cabeçalho e o rodapé só não mostram a marca. */
const ERRO_LOCAL = () => new HttpContext().set(HANDLE_HTTP_ERROR_LOCALLY, true);

/** A resposta de `GET publico/identidade`. */
export interface IdentidadePublicaResponse {
  desNomeEnte: string | null;
  desNomeCurto: string | null;
  bitPossuiBrasao: boolean;
  /** Muda sempre que o brasão publicado muda. Ausente na API anterior a ela. */
  desVersaoBrasao?: string | null;
}

function textoOuNulo(texto: string | null | undefined): string | null {
  const limpo = texto?.trim();
  return limpo ? limpo : null;
}

/**
 * A URL da imagem com a versão do brasão publicado. A imagem tem cache de 1 hora no
 * navegador, e a URL sem versão é a mesma para todo brasão: sem o `?v=`, quem já tinha
 * baixado o brasão antigo continuaria com ele depois da troca no adm.
 */
export function urlDoBrasao(versao: string | null | undefined): string {
  const limpa = textoOuNulo(versao);

  return limpa ? `${URL_BRASAO_PUBLICO}?v=${encodeURIComponent(limpa)}` : URL_BRASAO_PUBLICO;
}

/**
 * O nome e o brasão que o portal do órgão publica: os personalizados no Cabeçalho do adm
 * ou, no padrão, os do cadastro do órgão no SAI — a API decide. É raiz: o cabeçalho e o
 * rodapé dividem uma requisição por carregamento do portal. O portal não envia o órgão:
 * o back-end o resolve pelo host.
 *
 * Carregando, com erro ou sem cadastro, tudo vale `null`: a marca fica vazia, e nunca com
 * o nome de outro município.
 */
@Service()
export class IdentidadePortalService {
  private readonly identidadeApi = httpResource<ResponseApi<IdentidadePublicaResponse>>(() => ({
    url: RECURSO,
    context: ERRO_LOCAL(),
  }));

  /** O `error()` é consultado antes do `value()`, que lança em estado de erro. */
  private readonly identidade = computed<IdentidadePublicaResponse | null>(() => {
    if (this.identidadeApi.error() !== undefined) return null;

    return this.identidadeApi.value()?.data ?? null;
  });

  readonly carregando = computed(() => this.identidadeApi.isLoading());

  readonly nome = computed(() => textoOuNulo(this.identidade()?.desNomeEnte));

  /** Só existe junto do nome personalizado; nas telas estreitas, entra no lugar do nome. */
  readonly nomeCurto = computed(() => textoOuNulo(this.identidade()?.desNomeCurto));

  readonly brasaoUrl = computed(() => {
    const identidade = this.identidade();

    return identidade?.bitPossuiBrasao ? urlDoBrasao(identidade.desVersaoBrasao) : null;
  });
}
